<template>
  <floating-menu :disabled="saving || !profile">
    <template #button>
      <b-badge :variant="`${light ? 'light-' : ''}${resolveVariant(value)}`">
        {{ label }}
        <feather-icon icon="ChevronDownIcon" size="12" />
      </b-badge>
    </template>

    <template #default="{ close }">
      <a
        v-for="option in options"
        :key="option.value"
        href="#"
        class="dropdown-item"
        :class="{ active: option.value === value }"
        @click.prevent="
          close();
          update(option.value);
        "
      >
        {{ option.label }}
      </a>
    </template>
  </floating-menu>
</template>

<script>
import { BBadge } from 'bootstrap-vue';
import FloatingMenu from './FloatingMenu.vue';
import store from '@/store';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { title } from '@core/utils/filter';

/**
 * A single roster profile field, editable in place from a list (DEV-735,
 * DEV-738). Edits either a top-level field (`field`, e.g. status) or one of the
 * profile's attributes (`attribute`, e.g. uploadedCoe), and updates the row
 * directly on success. The API still enforces its own rules -- a refused change
 * (such as enrolling before acceptance) is shown as the API's message.
 */
export default {
  name: 'ProfileFieldSelect',
  components: { BBadge, FloatingMenu },
  props: {
    /** The roster profile (must carry `id` and, for attributes, `attributes`). */
    profile: { type: Object, default: null },
    /** Top-level field to edit. Use this or `attribute`. */
    field: { type: String, default: null },
    /** Attribute (under `profile.attributes`) to edit. Use this or `field`. */
    attribute: { type: String, default: null },
    /** [{ label, value }] in display order. */
    options: { type: Array, required: true },
    /** Badge colour for a value. */
    resolveVariant: { type: Function, default: () => 'secondary' },
    /** Use the light badge style. */
    light: { type: Boolean, default: true },
    /** What is being changed, for the failure message. */
    name: { type: String, default: 'profile' },
  },
  emits: ['updated'],
  data() {
    return { saving: false };
  },
  computed: {
    value() {
      if (!this.profile) return undefined;
      if (this.attribute) return this.profile.attributes ? this.profile.attributes[this.attribute] : undefined;
      return this.profile[this.field];
    },
    label() {
      const option = this.options.find((entry) => entry.value === this.value);
      return option ? option.label : title(this.value || 'N/A');
    },
  },
  methods: {
    update(value) {
      if (value === this.value) return;

      const profileData = this.attribute
        ? { attributes: { ...this.profile.attributes, [this.attribute]: value } }
        : { [this.field]: value };

      this.saving = true;
      store
        .dispatch('rosterStoreModule/updateProfile', { id: this.profile.id, profileData })
        .then(() => {
          if (this.attribute) this.$set(this.profile.attributes, this.attribute, value);
          else this.$set(this.profile, this.field, value);
          this.$emit('updated', value);
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `Failed to update ${this.name}`,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error.response && error.response.data ? error.response.data.message : error.message,
            },
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>
